// src/games/darkforest/scenes/clearing.js
import { DIFFICULTY_CLASS } from '../../../game-system/skills/skillSystem';

export const clearing = {
  mysterious_clearing: {
    id: 'mysterious_clearing',
    title: "Mysterious Forest Clearing",
    description: `After pushing through dense undergrowth, you emerge into a circular clearing bathed in dappled sunlight. The trees around the clearing are unusually large and ancient, their massive trunks covered in moss and strange symbols carved into the bark.

In the center of the clearing stands a weathered stone altar, its surface etched with the same symbols you saw on the trees. A soft blue glow emanates from small cracks in the stone.

The air here feels different - charged with some kind of energy that makes the hairs on your arms stand up. There's an unusual silence too, as if the forest creatures avoid this place.`,
    choices: [
      {
        id: 'examine_altar',
        text: "Approach and examine the altar",
        nextScene: 'stone_altar',
        requires: null
      },
      {
        id: 'examine_symbols',
        text: "Study the symbols on the trees",
        nextScene: 'tree_symbols',
        requires: null
      },
      {
        id: 'search_clearing',
        text: "Search the clearing for anything unusual",
        nextScene: 'clearing_search',
        requires: null
      },
      {
        id: 'leave_clearing',
        text: "This place feels dangerous - leave the clearing",
        nextScene: 'forest_path_fork',
        requires: null
      }
    ]
  },
  
  stone_altar: {
    id: 'stone_altar',
    title: "Ancient Stone Altar",
    description: `You approach the stone altar in the center of the clearing. Up close, you can see that it's extremely old, the stone worn smooth in places by countless years of weather and use.

The symbols carved into its surface form patterns that seem to shift slightly when you're not looking directly at them. The blue glow comes from thin veins of crystal embedded in the stone, pulsing gently like a heartbeat.

On top of the altar is a shallow basin filled with clear water that doesn't seem to evaporate despite the sunlight warming the stone. The water reflects your face, but something about the reflection seems... off, somehow.`,
    skillChecks: [
      {
        skill: 'arcana',
        difficultyClass: DIFFICULTY_CLASS.MEDIUM,
        description: "Analyze the magical properties of the altar",
        results: {
          success: "You recognize this as an ancient focus point for natural magic. Such altars were used by druidic circles to commune with forest spirits and channel the energy of the land. The blue crystals are conduits for magical energy, and the water in the basin is likely enchanted to show visions.",
          rewards: {
            experience: 30,
            items: ['scroll_identify']
          },
          failure: "The altar is clearly magical, but its exact purpose eludes you. The symbols seem to be related to nature magic, but you can't decipher their specific meanings."
        }
      },
      {
        skill: 'religion',
        difficultyClass: DIFFICULTY_CLASS.HARD,
        description: "Recall any religious significance of this altar",
        results: {
          success: "This altar bears hallmarks of the Old Faith, a nature-focused religion that predates many modern belief systems. It was likely used for seasonal rituals and communion with forest spirits, not sacrifices as some myths suggest.",
          rewards: {
            experience: 35
          },
          failure: "The altar doesn't match any religious iconography you're familiar with, though it clearly held ritual significance to whoever built it."
        }
      }
    ],
    choices: [
      {
        id: 'touch_water',
        text: "Touch the water in the basin",
        nextScene: 'altar_water',
        requires: null
      },
      {
        id: 'place_item',
        text: "Place an offering on the altar",
        nextScene: 'altar_offering',
        requires: null
      },
      {
        id: 'examine_symbols',
        text: "Study the symbols on the altar more closely",
        nextScene: 'altar_symbols',
        requires: null
      },
      {
        id: 'return_to_clearing',
        text: "Step back and return to the clearing",
        nextScene: 'mysterious_clearing',
        requires: null
      }
    ]
  },
  
  altar_water: {
    id: 'altar_water',
    title: "Visions in the Water",
    description: `You reach out and touch the surface of the water in the basin. It's unnaturally cool against your fingertip, and ripples spread outward from your touch, distorting your reflection.

As the ripples settle, your reflection changes. Instead of your current self, you see yourself as you might be in the future - equipped with items you don't yet possess, marked by experiences you haven't yet had.

The vision shifts again, showing flashes of other scenes: a ruined temple deep in the forest, a brilliant crystal glowing in darkness, shadowy figures gathered around a much larger altar, and finally, yourself standing at a crossroads, facing a choice.

The visions fade, leaving just ordinary water reflecting the sky above.`,
    choices: [
      {
        id: 'ponder_vision',
        text: "Try to make sense of the visions",
        nextScene: 'interpret_vision',
        requires: null
      },
      {
        id: 'place_item',
        text: "Place an offering on the altar",
        nextScene: 'altar_offering',
        requires: null
      },
      {
        id: 'return_to_clearing',
        text: "Step back from the altar",
        nextScene: 'mysterious_clearing',
        requires: null
      }
    ]
  },
  
  interpret_vision: {
    id: 'interpret_vision',
    title: "Interpreting the Vision",
    description: `You stand before the altar, trying to make sense of the visions you saw in the water.`,
    skillChecks: [
      {
        skill: 'insight',
        difficultyClass: DIFFICULTY_CLASS.HARD,
        description: "Interpret the meaning of your visions",
        results: {
          success: "You realize the visions were showing you your potential path through the forest. The temple ruins must be your destination, and the crystal likely the artifact you're seeking. The shadowy figures suggest others are also searching for it, and the crossroads represents a significant choice you'll need to make before your journey's end.",
          rewards: {
            experience: 40,
            storyFlags: ['vision_understood']
          },
          failure: "While the visions clearly held meaning, you can't quite piece together what they were trying to tell you. Perhaps more exploration will clarify their significance."
        }
      }
    ],
    choices: [
      {
        id: 'place_item',
        text: "Place an offering on the altar",
        nextScene: 'altar_offering',
        requires: null
      },
      {
        id: 'return_to_clearing',
        text: "Step back from the altar",
        nextScene: 'mysterious_clearing',
        requires: null
      }
    ]
  },
  
  altar_offering: {
    id: 'altar_offering',
    title: "Making an Offering",
    description: `You decide to place an offering on the altar, following ancient custom. What will you offer?`,
    inventory_choices: [
      {
        item_id: 'minor_healing_potion',
        text: "Offer a healing potion",
        nextScene: 'offering_accepted',
        result: {
          description: "You place the healing potion on the altar. For a moment, nothing happens. Then the liquid inside the vial begins to glow, matching the blue light of the altar crystals. The potion is absorbed into the stone, leaving the empty vial behind. A feeling of warmth spreads through your body, and you realize any minor injuries you had have been healed.",
          rewards: {
            healPercentage: 50,
            experience: 30
          }
        }
      },
      {
        item_id: 'mysterious_crystal',
        text: "Offer the mysterious crystal",
        nextScene: 'offering_resonance',
        result: {
          description: "As you place the mysterious crystal on the altar, it begins to resonate with the blue crystals embedded in the stone. The glow intensifies dramatically, and the symbols on the altar shine with brilliant light. The crystal floats above the altar, spinning slowly, before settling back down - now pulsing with the same rhythm as the altar itself. You sense that it has been attuned to this place somehow.",
          rewards: {
            removeItem: 'mysterious_crystal',
            addItem: 'attuned_crystal',
            experience: 50,
            storyFlags: ['crystal_attuned']
          }
        }
      },
      {
        item_id: 'any_food',
        text: "Offer some food",
        nextScene: 'offering_accepted',
        result: {
          description: "You place some food on the altar. It slowly dissolves into the stone, leaving no trace behind. A gentle breeze rustles through the clearing, carrying the scent of flowers and fresh growth. You feel more alert and refreshed, as if you've just had a good meal and rest.",
          rewards: {
            temporaryBonus: {
              skill: 'perception',
              duration: 'short',
              value: 2
            },
            experience: 20
          }
        }
      }
    ],
    choices: [
      {
        id: 'no_offering',
        text: "Decide not to make an offering after all",
        nextScene: 'altar_no_offering',
        requires: null
      },
      {
        id: 'return_to_clearing',
        text: "Step back from the altar",
        nextScene: 'mysterious_clearing',
        requires: null
      }
    ]
  },
  
  altar_no_offering: {
    id: 'altar_no_offering',
    title: "No Offering",
    description: `You consider making an offering but decide against it. Either you have nothing appropriate to give, or you're uncertain about the consequences of interacting further with this clearly magical altar.

As you withdraw your hand, the blue glow of the altar dims slightly, as if in disappointment.`,
    choices: [
      {
        id: 'touch_water',
        text: "Touch the water in the basin instead",
        nextScene: 'altar_water',
        requires: null
      },
      {
        id: 'examine_symbols',
        text: "Study the symbols on the altar",
        nextScene: 'altar_symbols',
        requires: null
      },
      {
        id: 'return_to_clearing',
        text: "Step back from the altar",
        nextScene: 'mysterious_clearing',
        requires: null
      }
    ]
  },
  
  altar_symbols: {
    id: 'altar_symbols',
    title: "Ancient Symbols",
    description: `You lean closer to study the intricate symbols carved into the altar's surface. They form patterns that seem to flow around the central basin, depicting stylized trees, animals, and celestial bodies.

Some of the symbols appear to represent the phases of the moon, while others show what might be constellations. There are also what looks like scenes of ritual gatherings around similar altars, with figures raising their hands toward a full moon.`,
    skillChecks: [
      {
        skill: 'history',
        difficultyClass: DIFFICULTY_CLASS.HARD,
        description: "Recognize the historical origins of these symbols",
        results: {
          success: "You recognize these as symbols used by the ancient forest dwellers known as the Sylvan. Their civilization thrived in these woods thousands of years ago, living in harmony with nature and developing a sophisticated understanding of natural magic. These altars served as focal points for their seasonal celebrations and communion with the forest.",
          rewards: {
            experience: 35,
            storyFlags: ['sylvan_knowledge']
          },
          failure: "While you can tell these symbols are ancient, their specific cultural origins remain a mystery to you."
        }
      },
      {
        skill: 'investigation',
        difficultyClass: DIFFICULTY_CLASS.MEDIUM,
        description: "Look for any hidden messages or mechanisms",
        results: {
          success: "As you examine the symbols carefully, you notice that some can be rotated slightly. Testing your theory, you find that turning specific symbols in a particular sequence causes a small compartment to open at the base of the altar. Inside, you find a small, polished stone amulet carved with symbols matching those on the altar.",
          rewards: {
            items: ['forest_amulet'],
            experience: 30
          },
          failure: "The symbols seem purely decorative or ritual in nature. If there are any hidden components to the altar, you can't find them."
        }
      }
    ],
    choices: [
      {
        id: 'touch_water',
        text: "Touch the water in the basin",
        nextScene: 'altar_water',
        requires: null
      },
      {
        id: 'place_item',
        text: "Place an offering on the altar",
        nextScene: 'altar_offering',
        requires: null
      },
      {
        id: 'return_to_clearing',
        text: "Step back from the altar",
        nextScene: 'mysterious_clearing',
        requires: null
      }
    ]
  },
  
  tree_symbols: {
    id: 'tree_symbols',
    title: "Symbols on the Trees",
    description: `You turn your attention to the massive trees surrounding the clearing. Each trunk bears carved symbols similar to those on the central altar, though simpler in design. The carvings are old, with moss and lichen growing in many of them, yet they remain clearly visible.

The symbols form a continuous pattern around the entire clearing, as if creating a boundary or protective circle. You notice that they're positioned at equal distances from each other, suggesting deliberate placement.`,
    skillChecks: [
      {
        skill: 'nature',
        difficultyClass: DIFFICULTY_CLASS.MEDIUM,
        description: "Determine how the carvings affect the trees",
        results: {
          success: "Remarkably, the trees show no signs of damage or disease despite the deep carvings in their bark. In fact, they appear especially healthy and vibrant. You realize the symbols were carved using a method that worked with the trees' natural growth, suggesting a deep understanding of forest life by whoever created them.",
          rewards: {
            experience: 25
          },
          failure: "The trees appear unusually healthy despite the carvings in their bark, but you can't determine any special significance to this."
        }
      }
    ],
    choices: [
      {
        id: 'trace_symbol',
        text: "Trace one of the symbols with your finger",
        nextScene: 'trace_tree_symbol',
        requires: null
      },
      {
        id: 'examine_altar',
        text: "Approach and examine the altar",
        nextScene: 'stone_altar',
        requires: null
      },
      {
        id: 'search_clearing',
        text: "Search the clearing for anything unusual",
        nextScene: 'clearing_search',
        requires: null
      },
      {
        id: 'return_to_forest',
        text: "Return to the forest path",
        nextScene: 'dense_forest',
        requires: null
      }
    ]
  },
  
  trace_tree_symbol: {
    id: 'trace_tree_symbol',
    title: "Tracing the Symbol",
    description: `You approach one of the trees and carefully trace the carved symbol with your finger. As your skin makes contact with the carving, you feel a subtle vibration and warmth. The symbol briefly glows with a faint blue light similar to the altar's crystals.

A strange sensation washes over you - a fleeting impression of deep roots spreading beneath the soil, of sunlight filtering through leaves, of seasons changing in rapid succession. For just a moment, you perceive the forest from the tree's perspective - ancient, patient, and alive in ways you hadn't imagined.

The sensation fades as you withdraw your hand, leaving you with a profound sense of connection to the forest.`,
    choices: [
      {
        id: 'examine_altar',
        text: "Approach and examine the altar",
        nextScene: 'stone_altar',
        requires: null
      },
      {
        id: 'search_clearing',
        text: "Search the clearing for anything unusual",
        nextScene: 'clearing_search',
        requires: null
      },
      {
        id: 'return_to_forest',
        text: "Return to the forest path",
        nextScene: 'dense_forest',
        requires: null
      }
    ],
    rewards: {
      experience: 20,
      storyFlags: ['tree_connection']
    }
  },
  
  clearing_search: {
    id: 'clearing_search',
    title: "Searching the Clearing",
    description: `You carefully explore the circular clearing, examining the ground, the trees, and the area around the stone altar for anything unusual or potentially useful.`,
    skillChecks: [
      {
        skill: 'investigation',
        difficultyClass: DIFFICULTY_CLASS.HARD,
        description: "Search thoroughly for hidden items or clues",
        results: {
          success: "Near the base of one of the trees, you find a small hollow concealed by moss. Inside is a weathered leather pouch containing several dried herbs that give off a pleasant, medicinal aroma, and a small stone vial of greenish liquid.",
          rewards: {
            items: ['antitoxin'],
            experience: 30
          },
          failure: "Despite a careful search, you don't find anything beyond what's immediately visible in the clearing."
        }
      },
      {
        skill: 'perception',
        difficultyClass: DIFFICULTY_CLASS.MEDIUM,
        description: "Notice subtle details about the clearing",
        results: {
          success: "You notice that certain plants grow in specific patterns around the clearing - not random, but in carefully maintained arrangements. Some of these plants are rare medicinal herbs. Whoever tends this clearing still visits, though perhaps not frequently.",
          rewards: {
            experience: 20
          },
          failure: "The clearing appears natural, if unusually symmetrical. Nothing particularly notable catches your eye beyond the altar and carved trees."
        }
      }
    ],
    choices: [
      {
        id: 'examine_altar',
        text: "Approach and examine the altar",
        nextScene: 'stone_altar',
        requires: null
      },
      {
        id: 'examine_symbols',
        text: "Study the symbols on the trees",
        nextScene: 'tree_symbols',
        requires: null
      },
      {
        id: 'leave_clearing',
        text: "Leave the clearing and return to the forest",
        nextScene: 'dense_forest',
        requires: null
      }
    ]
  },
  
  offering_accepted: {
    id: 'offering_accepted',
    title: "Offering Accepted",
    description: `Your offering is accepted by the altar, disappearing into the stone with a soft blue glow. A sense of peaceful energy radiates outward, and you feel somehow more connected to the forest around you.

The water in the basin ripples without your touch, and for a brief moment, you see a new image reflected there - a path leading deeper into the forest, toward ancient ruins bathed in moonlight.`,
    choices: [
      {
        id: 'follow_vision',
        text: "Follow the path shown in the vision",
        nextScene: 'ancient_path',
        requires: null
      },
      {
        id: 'return_to_clearing',
        text: "Step back from the altar",
        nextScene: 'mysterious_clearing',
        requires: null
      }
    ]
  },
  
  offering_resonance: {
    id: 'offering_resonance',
    title: "Crystal Resonance",
    description: `The mysterious crystal resonates powerfully with the altar, creating a spectacular display of magical energy. The entire clearing is bathed in blue light, and the symbols on the trees all begin to glow in sequence, creating a rippling circle of light around you.

When the light fades, your crystal has transformed, now pulsing with the same energy as the altar itself. You sense that it has become attuned to this place and may serve as a key or guide for what lies ahead.

The water in the basin shows a clear vision - a path through the forest leading to ancient ruins, and a doorway that requires a crystal key to open.`,
    choices: [
      {
        id: 'follow_vision',
        text: "Follow the path shown in the vision",
        nextScene: 'ancient_path',
        requires: null
      },
      {
        id: 'return_to_clearing',
        text: "Step back from the altar",
        nextScene: 'mysterious_clearing',
        requires: null
      }
    ]
  },
  
  ancient_path: {
    id: 'ancient_path',
    title: "Path to the Ruins",
    description: `Guided by the vision from the altar, you discover a narrow, overgrown path leading deeper into the forest. Unlike the main trails, this path is nearly invisible unless you know to look for it, marked only by occasional stones bearing faint carvings similar to those in the clearing.

As you follow the path, the forest grows older and more primeval. The trees here are enormous, their huge roots creating natural archways and barriers. Shafts of sunlight pierce the canopy in places, illuminating carpets of unusual flowers that seem to turn toward you as you pass.

There's a watchful quality to the silence here, as if the forest itself is observing your progress.`,
    choices: [
      {
        id: 'continue_to_ruins',
        text: "Continue toward the ruins",
        nextScene: 'ruins_approach',
        requires: null
      },
      {
        id: 'collect_flowers',
        text: "Examine the unusual flowers",
        nextScene: 'ancient_flowers',
        requires: null
      },
      {
        id: 'return_to_clearing',
        text: "Return to the clearing",
        nextScene: 'mysterious_clearing',
        requires: null
      }
    ]
  }
};